import { Alert, Button, Drawer, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import { Eye, EyeOff, Megaphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { useManageHighlightedNews } from '../hooks/useManageHighlightedNews'
import { useToggleNewsActive } from '../hooks/useToggleNewsActive'

export function DrawerManageHighlightedNews() {
  const { t } = useTranslation('panel')
  const { activeNewsHighlighted, inactiveNewsHighlighted } =
    useManageHighlightedNews()
  const drawerManageHighlightedNews = useDrawer(
    'drawer-manage-highlighted-news',
  )
  const drawerCreateNewsHighlighted = useDrawer(
    'drawer-create-news-highlighted',
  )
  const { userInfo } = useGetUserInfo()
  const { toggleNewsActive } = useToggleNewsActive()

  const handleCreateNewsHighlighted = () => {
    drawerManageHighlightedNews.close()
    drawerCreateNewsHighlighted.open()
  }

  return (
    <Drawer.Root id="drawer-manage-highlighted-news">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('news.drawer_manage_highlighted_news.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl text-brand-800 font-semibold">
              {t('news.drawer_manage_highlighted_news.body.title')}
            </h1>

            <span className="text-sm text-brand-800 font-light">
              {t('news.drawer_manage_highlighted_news.body.tip')}
            </span>
          </div>

          {userInfo?.UserPreference?.allowedPosts && (
            <Button.Root onClick={handleCreateNewsHighlighted}>
              <Button.Icon icon={Megaphone} />
              {t('news.drawer_manage_highlighted_news.header.new_highlighted')}
            </Button.Root>
          )}
        </div>

        <div className="flex flex-col flex-1 gap-4 ">
          <h2 className="text-lg text-brand-800 font-semibold">
            {t('news.drawer_manage_highlighted_news.body.active_news')}
          </h2>

          <div className="flex flex-col gap-2 flex-1">
            {activeNewsHighlighted.map((news) => (
              <Alert.Root
                color="success"
                key={news.id}
                className="w-full select-none"
              >
                <Alert.Description className="flex items-center justify-between gap-2 w-full">
                  <span>
                    {news?.title ||
                      news?.subTitle ||
                      news?.postFile?.content ||
                      news?.postExternalUrl}
                  </span>

                  <Tooltip
                    text={t(
                      'news.drawer_manage_highlighted_news.body.inactivate_tooltip',
                    )}
                  >
                    <button
                      type="button"
                      className="hover:text-danger-600 transition-colors"
                      onClick={() => toggleNewsActive(news)}
                    >
                      <Alert.Icon icon={<EyeOff />} />
                    </button>
                  </Tooltip>
                </Alert.Description>
              </Alert.Root>
            ))}
          </div>

          <h2 className="text-lg text-brand-800 font-semibold">
            {t('news.drawer_manage_highlighted_news.body.inactive_news')}
          </h2>

          <div className="flex flex-col gap-2 flex-1">
            {inactiveNewsHighlighted.map((news) => (
              <Alert.Root
                color="secondary"
                key={news.id}
                className="w-full select-none"
              >
                <Alert.Description className="flex items-center justify-between gap-2 w-full">
                  <span>
                    {news?.title ||
                      news?.subTitle ||
                      news?.postFile?.content ||
                      news?.postExternalUrl}
                  </span>

                  <Tooltip
                    text={t(
                      'news.drawer_manage_highlighted_news.body.activate_tooltip',
                    )}
                  >
                    <button
                      type="button"
                      className="hover:text-brand-600 transition-colors"
                      onClick={() => toggleNewsActive(news)}
                    >
                      <Alert.Icon icon={<Eye />} />
                    </button>
                  </Tooltip>
                </Alert.Description>
              </Alert.Root>
            ))}
          </div>
        </div>
      </Drawer.Body>

      <Drawer.Footer>
        <Button.Root
          variant="secondary"
          color="brand"
          onClick={drawerManageHighlightedNews.close}
        >
          {t('news.drawer_manage_highlighted_news.footer.button_close')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
